// src/pages/Plantation/tabs/SureteTab.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Eye, Edit2, Trash2, Plus, X, AlertCircle, RefreshCw,
} from 'lucide-react';
import {
  plantationService,
  PlantationSurete,
  PlantationCampagne,
  SureteZone,
  SureteIntervention,
} from '../../../services/plantation.service';
import toast from '../../../utils/toast';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const STATUT_BADGE: Record<PlantationSurete['statut'], string> = {
  planifie:  'bg-gray-100 text-gray-600',
  en_cours:  'bg-blue-100 text-blue-700',
  termine:   'bg-emerald-100 text-emerald-700',
};

const STATUT_LABEL: Record<PlantationSurete['statut'], string> = {
  planifie:  'Planifié',
  en_cours:  'En cours',
  termine:   'Terminé',
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  entiteId: string;
  campagnes: PlantationCampagne[];
  campagneActive?: PlantationCampagne;
}

// ── Modal détail (lecture seule) ──────────────────────────────────────────────

interface ModalDetailProps {
  surete: PlantationSurete;
  campagnes: PlantationCampagne[];
  onClose: () => void;
}

const ModalDetail: React.FC<ModalDetailProps> = ({ surete, campagnes, onClose }) => {
  const campagne = campagnes.find(c => c.id === surete.campagneId);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-800">Détail ronde de sûreté — {fmtDate(surete.date)}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-5">
          {/* Identification */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Identification</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ['Date', fmtDate(surete.date)],
                ['Statut', STATUT_LABEL[surete.statut]],
                ['Agent (matricule)', surete.matriculeAgent || '—'],
                ['Conducteur', surete.nomConducteur || '—'],
                ['Véhicule', surete.immatriculation || 'À pied'],
                ['Campagne', campagne?.libelle || '—'],
                ['Heure départ', surete.heureDepart || '—'],
                ['Heure arrivée', surete.heureArrivee || '—'],
                ['VRA (FCFA)', surete.vra != null ? surete.vra.toLocaleString('fr-FR').replace(/ /g, ' ') : '—'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between border-b border-gray-50 pb-1">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-800">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Parcours zones */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">
              Parcours zones ({(surete.parcoursZones || []).length})
            </h4>
            {(surete.parcoursZones || []).length === 0 ? (
              <p className="text-sm text-gray-400 italic">Aucune zone enregistrée</p>
            ) : (
              <div className="space-y-2">
                {(surete.parcoursZones || []).map((z, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 text-sm grid grid-cols-2 gap-2">
                    <div><span className="text-gray-500">Bloc/Zone : </span><span className="font-medium">{z.bloc}</span></div>
                    {z.heure && <div><span className="text-gray-500">Heure : </span><span>{z.heure}</span></div>}
                    {z.action && <div className="col-span-2"><span className="text-gray-500">Action : </span><span>{z.action}</span></div>}
                    {z.observations && <div className="col-span-2"><span className="text-gray-500">Observations : </span><span>{z.observations}</span></div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Interventions */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">
              Interventions ({(surete.interventions || []).length})
            </h4>
            {(surete.interventions || []).length === 0 ? (
              <p className="text-sm text-gray-400 italic">Aucune intervention</p>
            ) : (
              <div className="space-y-2">
                {(surete.interventions || []).map((inv, i) => (
                  <div key={i} className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-sm grid grid-cols-2 gap-2">
                    <div><span className="text-gray-500">Bloc : </span><span className="font-medium">{inv.bloc}</span></div>
                    {inv.heure && <div><span className="text-gray-500">Heure : </span><span>{inv.heure}</span></div>}
                    <div className="col-span-2"><span className="text-gray-500">Action menée : </span><span>{inv.actionMenee}</span></div>
                    {inv.saisies && <div className="col-span-2"><span className="text-gray-500">Saisies : </span><span>{inv.saisies}</span></div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rapport */}
          {(surete.anomaliesDetectees || surete.rapport) && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Rapport</h4>
              {surete.anomaliesDetectees && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-red-600 mb-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" /> Anomalies détectées
                  </p>
                  <p className="text-sm text-gray-700 bg-red-50 rounded-lg px-3 py-2">{surete.anomaliesDetectees}</p>
                </div>
              )}
              {surete.rapport && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Rapport général</p>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 whitespace-pre-wrap">{surete.rapport}</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex justify-end flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal création / édition ──────────────────────────────────────────────────

interface ModalFormProps {
  entiteId: string;
  campagnes: PlantationCampagne[];
  editing: PlantationSurete | null;
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_ZONE: SureteZone = { bloc: '', heure: '', action: '', observations: '' };
const EMPTY_INTERVENTION: SureteIntervention = { bloc: '', actionMenee: '', saisies: '', heure: '' };

const ModalForm: React.FC<ModalFormProps> = ({ entiteId, campagnes, editing, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState<Partial<PlantationSurete>>(editing ?? {
    date: today,
    statut: 'planifie',
    parcoursZones: [],
    interventions: [],
    entiteId,
  });
  const [zones, setZones] = useState<SureteZone[]>(editing?.parcoursZones ?? []);
  const [interventions, setInterventions] = useState<SureteIntervention[]>(editing?.interventions ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k: keyof PlantationSurete, v: any) => setForm(f => ({ ...f, [k]: v }));

  // Zones helpers
  const addZone = () => setZones(z => [...z, { ...EMPTY_ZONE }]);
  const removeZone = (i: number) => setZones(z => z.filter((_, idx) => idx !== i));
  const setZoneField = (i: number, k: keyof SureteZone, v: string) =>
    setZones(z => z.map((zone, idx) => idx === i ? { ...zone, [k]: v } : zone));

  // Interventions helpers
  const addIntervention = () => setInterventions(iv => [...iv, { ...EMPTY_INTERVENTION }]);
  const removeIntervention = (i: number) => setInterventions(iv => iv.filter((_, idx) => idx !== i));
  const setInvField = (i: number, k: keyof SureteIntervention, v: string) =>
    setInterventions(iv => iv.map((item, idx) => idx === i ? { ...item, [k]: v } : item));

  const handleSave = async () => {
    setError('');
    if (!form.date) { setError('La date est obligatoire.'); return; }
    setLoading(true);
    try {
      const payload: Partial<PlantationSurete> = {
        ...form,
        entiteId,
        parcoursZones: zones,
        interventions,
      };
      if (editing) {
        await plantationService.updateSurete(editing.id, payload);
        toast.success('Ronde mise à jour');
      } else {
        await plantationService.createSurete(payload);
        toast.success('Ronde créée');
      }
      onSaved();
      onClose();
    } catch {
      setError('Erreur lors de la sauvegarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-800">
              {editing ? 'Modifier la ronde' : 'Nouvelle ronde de sûreté'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          {/* Section 1 — Identification */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Identification</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.date || ''} onChange={e => set('date', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.statut || 'planifie'} onChange={e => set('statut', e.target.value as PlantationSurete['statut'])}>
                  <option value="planifie">Planifié</option>
                  <option value="en_cours">En cours</option>
                  <option value="termine">Terminé</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Matricule agent sûreté</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.matriculeAgent || ''} onChange={e => set('matriculeAgent', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Immatriculation véhicule</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Vide si ronde à pied"
                  value={form.immatriculation || ''} onChange={e => set('immatriculation', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nom conducteur</label>
                <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.nomConducteur || ''} onChange={e => set('nomConducteur', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Campagne</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.campagneId || ''} onChange={e => set('campagneId', e.target.value || undefined)}>
                  <option value="">— Aucune —</option>
                  {campagnes.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Heure départ</label>
                <input type="time" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.heureDepart || ''} onChange={e => set('heureDepart', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Heure arrivée</label>
                <input type="time" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.heureArrivee || ''} onChange={e => set('heureArrivee', e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Budget restant alloué (FCFA)</label>
                <input type="number" step="1" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.vra ?? ''} onChange={e => set('vra', e.target.value ? parseFloat(e.target.value) : undefined)} />
              </div>
            </div>
          </div>

          {/* Section 2 — Parcours zones */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase">Parcours zones</h4>
              <button type="button" onClick={addZone}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                <Plus className="w-3.5 h-3.5" /> Ajouter une zone
              </button>
            </div>
            {zones.length === 0 && (
              <p className="text-xs text-gray-400 italic">Aucune zone. Cliquez sur "Ajouter une zone".</p>
            )}
            <div className="space-y-3">
              {zones.map((z, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3 grid grid-cols-2 gap-3 relative">
                  <button type="button" onClick={() => removeZone(i)}
                    className="absolute top-2 right-2 p-1 rounded text-gray-300 hover:text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Bloc / Zone *</label>
                    <input className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={z.bloc} onChange={e => setZoneField(i, 'bloc', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Heure</label>
                    <input type="time" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={z.heure || ''} onChange={e => setZoneField(i, 'heure', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Action effectuée</label>
                    <input className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={z.action || ''} onChange={e => setZoneField(i, 'action', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Observations</label>
                    <input className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={z.observations || ''} onChange={e => setZoneField(i, 'observations', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3 — Interventions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase">Interventions</h4>
              <button type="button" onClick={addIntervention}
                className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium">
                <Plus className="w-3.5 h-3.5" /> Ajouter une intervention
              </button>
            </div>
            {interventions.length === 0 && (
              <p className="text-xs text-gray-400 italic">Aucune intervention. Cliquez sur "Ajouter une intervention".</p>
            )}
            <div className="space-y-3">
              {interventions.map((inv, i) => (
                <div key={i} className="bg-amber-50 border border-amber-100 rounded-lg p-3 grid grid-cols-2 gap-3 relative">
                  <button type="button" onClick={() => removeIntervention(i)}
                    className="absolute top-2 right-2 p-1 rounded text-gray-300 hover:text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Bloc *</label>
                    <input className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={inv.bloc} onChange={e => setInvField(i, 'bloc', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Heure</label>
                    <input type="time" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={inv.heure || ''} onChange={e => setInvField(i, 'heure', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Action menée *</label>
                    <input className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      value={inv.actionMenee} onChange={e => setInvField(i, 'actionMenee', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Saisies effectuées</label>
                    <input className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                      placeholder="ex: braconniers, matériel illicite…"
                      value={inv.saisies || ''} onChange={e => setInvField(i, 'saisies', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4 — Rapport */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Rapport</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Anomalies détectées</label>
                <textarea rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  value={form.anomaliesDetectees || ''}
                  onChange={e => set('anomaliesDetectees', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Rapport général</label>
                <textarea rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                  value={form.rapport || ''}
                  onChange={e => set('rapport', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={handleSave} disabled={loading}
            className="bg-blue-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'Enregistrement…' : editing ? 'Mettre à jour' : 'Créer la ronde'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

const SureteTab: React.FC<Props> = ({ entiteId, campagnes, campagneActive }) => {
  const today = new Date().toISOString().slice(0, 10);

  const [suretes, setSuretes] = useState<PlantationSurete[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filtres
  const [filtreDate, setFiltreDate] = useState(today);
  const [filtreCampagne, setFiltreCampagne] = useState(campagneActive?.id || '');
  const [filtreStatut, setFiltreStatut] = useState('');

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PlantationSurete | null>(null);
  const [viewing, setViewing] = useState<PlantationSurete | null>(null);
  const [interventionsDetail, setInterventionsDetail] = useState<PlantationSurete | null>(null);

  const load = useCallback(async () => {
    if (!entiteId) return;
    setLoading(true);
    setError('');
    try {
      const data = await plantationService.listSuretes(entiteId, {
        date: filtreDate || undefined,
        campagneId: filtreCampagne || undefined,
      });
      setSuretes(data);
    } catch {
      setError('Erreur lors du chargement des rondes de sûreté.');
    } finally {
      setLoading(false);
    }
  }, [entiteId, filtreDate, filtreCampagne]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer cette ronde de sûreté ?')) return;
    try {
      await plantationService.deleteSurete(id);
      toast.success('Ronde supprimée');
      load();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Filtrage local par statut
  const filtered = filtreStatut
    ? suretes.filter(s => s.statut === filtreStatut)
    : suretes;

  // KPIs
  const totalRondes = filtered.length;
  const enCours = filtered.filter(s => s.statut === 'en_cours').length;
  const terminees = filtered.filter(s => s.statut === 'termine').length;
  const totalInterventions = filtered.reduce((sum, s) => sum + (s.interventions || []).length, 0);

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total rondes', value: totalRondes, color: 'bg-gray-100 text-gray-600' },
          { label: 'En cours', value: enCours, color: 'bg-blue-100 text-blue-700' },
          { label: 'Terminées', value: terminees, color: 'bg-emerald-100 text-emerald-700' },
          { label: 'Interventions totales', value: totalInterventions, color: 'bg-amber-100 text-amber-700' },
        ].map(kpi => (
          <div key={kpi.label} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${kpi.color}`}>
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500">{kpi.label}</p>
              <p className="text-2xl font-bold text-gray-800">{kpi.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date</label>
          <input type="date" className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            value={filtreDate} onChange={e => setFiltreDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Campagne</label>
          <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            value={filtreCampagne} onChange={e => setFiltreCampagne(e.target.value)}>
            <option value="">Toutes</option>
            {campagnes.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Statut</label>
          <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}>
            <option value="">Tous</option>
            <option value="planifie">Planifié</option>
            <option value="en_cours">En cours</option>
            <option value="termine">Terminé</option>
          </select>
        </div>
        <button onClick={load}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Filtrer
        </button>
        <div className="ml-auto">
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="bg-blue-600 text-white rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-blue-700 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Nouvelle ronde
          </button>
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {/* Tableau */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                {['Date', 'Agent (matricule)', 'Véhicule', 'Conducteur', 'Départ → Arrivée', 'Zones couvertes', 'Interventions', 'Anomalies', 'Statut', 'Actions'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={10} className="text-center py-8 text-gray-400">Chargement…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-gray-400">
                    <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p>Aucune ronde de sûreté pour cette période</p>
                  </td>
                </tr>
              ) : filtered.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-medium text-gray-800 whitespace-nowrap">{fmtDate(s.date)}</td>
                  <td className="px-3 py-2.5 text-gray-600 font-mono text-xs">{s.matriculeAgent || '—'}</td>
                  <td className="px-3 py-2.5 text-gray-600">{s.immatriculation || <span className="text-gray-400 italic text-xs">À pied</span>}</td>
                  <td className="px-3 py-2.5 text-gray-600">{s.nomConducteur || '—'}</td>
                  <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap text-xs">
                    {s.heureDepart || '—'} → {s.heureArrivee || '—'}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-0.5 rounded">
                      {(s.parcoursZones || []).length}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {(s.interventions || []).length > 0 ? (
                      <button
                        onClick={() => setInterventionsDetail(s)}
                        className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded hover:bg-amber-200 transition-colors"
                        title="Voir les interventions"
                      >
                        {(s.interventions || []).length}
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {s.anomaliesDetectees ? (
                      <span title={s.anomaliesDetectees}><AlertCircle className="w-4 h-4 text-red-500 mx-auto" /></span>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap ${STATUT_BADGE[s.statut]}`}>
                      {STATUT_LABEL[s.statut]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      <button onClick={() => setViewing(s)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600" title="Voir détail">
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { setEditing(s); setShowForm(true); }}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-amber-600" title="Modifier">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(s.id)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600" title="Supprimer">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mini-modal interventions */}
      {interventionsDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setInterventionsDetail(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                Interventions — {fmtDate(interventionsDetail.date)}
              </h3>
              <button onClick={() => setInterventionsDetail(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-2">
              {(interventionsDetail.interventions || []).map((inv, i) => (
                <div key={i} className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-800">{inv.bloc}</span>
                    {inv.heure && <span className="text-gray-500 text-xs">{inv.heure}</span>}
                  </div>
                  <p className="text-gray-700 mt-0.5">{inv.actionMenee}</p>
                  {inv.saisies && <p className="text-xs text-amber-700 mt-0.5">Saisies : {inv.saisies}</p>}
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t flex-shrink-0 flex justify-end">
              <button onClick={() => setInterventionsDetail(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal détail */}
      {viewing && (
        <ModalDetail surete={viewing} campagnes={campagnes} onClose={() => setViewing(null)} />
      )}

      {/* Modal form */}
      {showForm && (
        <ModalForm
          entiteId={entiteId}
          campagnes={campagnes}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default SureteTab;
